import {
    deDE as coreDeDE,
    enUS as coreEnUS,
    frFR as coreFrFR,
    hiIN as coreHiIN,
    zhCN as coreZhCN,
    Localization
} from '@mui/material/locale';
import { deDE as gridDeDE, enUS as gridEnUS, frFR as gridFrFR, zhCN as gridZnCH } from '@mui/x-data-grid/locales';
import { Localization as GridLocalization } from '@mui/x-data-grid/utils/getGridLocalization';
import {
    deDE as pickersDeDE,
    enUS as pickersEnUS,
    frFR as pickersFrFR,
    zhCN as pickersZhCN
} from '@mui/x-date-pickers/locales';
import CurrencyList from 'currency-list';
import {
    de as dateFnsDeDE,
    enCA as dateFnsEnCA,
    enGB as dateFnsEnGB,
    enUS as dateFnsEnUS,
    fr as dateFnsFr,
    frCA as dateFnsFrCA,
    hi as dateFnsIn,
    zhCN as dateFnsZhCN,
    Locale
} from 'date-fns/locale';
import deDEBundle from './bundles/de-DE.json';
import enCABundle from './bundles/en-CA.json';
import enGBBundle from './bundles/en-GB.json';
import enUSBundle from './bundles/en-US.json';
import frCABundle from './bundles/fr-CA.json';
import frFRBundle from './bundles/fr-FR.json';
import hiINBundle from './bundles/hi-IN.json';
import zhCNBundle from './bundles/zh-CN.json';

const DEFAULT_LOCALE = 'en-US';

// currency-list resolves with different export shapes across webpack app runtime and
// vitest/browser test runtime (e.g. currencyList vs default.currencyList).
// Normalize once so locale map lookup stays stable and avoids import-time crashes.
const currencyListData =
    (CurrencyList as any)?.currencyList ??
    (CurrencyList as any)?.default?.currencyList ??
    (CurrencyList as any)?.default ??
    {};

const getCurrencyMap = (localeKey: string): CurrencyMap => {
    return (currencyListData?.default?.[localeKey] ?? currencyListData?.[localeKey] ?? {}) as CurrencyMap;
};

export type CurrencyDef = {
    name: string;
    symbol_native: string;
    symbol: string;
    code: string;
    name_plural: string;
    rounding: number;
    decimal_digits: number;
};

export type CurrencyMap = {
    [code: string]: CurrencyDef;
};

export type L10nDefType = {
    label: string;
    locale: string;
    dateFns: Locale;
    muiCore: Localization;
    muiPickers: any;
    muiGrid: GridLocalization;
    currencies: CurrencyMap;
    appBundle: object;
    defaultCurrency: string;
};

export type SupportedL10nType = {
    [key in string]: L10nDefType;
};

export const supportedL10n: SupportedL10nType = {
    'en-US': {
        label: 'English - US',
        locale: 'en-US',
        dateFns: dateFnsEnUS,
        muiCore: coreEnUS,
        muiPickers: pickersEnUS,
        muiGrid: gridEnUS,
        currencies: getCurrencyMap('en_US'),
        appBundle: enUSBundle,
        defaultCurrency: 'USD'
    },
    'en-GB': {
        label: 'English - UK',
        locale: 'en-GB',
        dateFns: dateFnsEnGB,
        muiCore: coreEnUS,
        muiPickers: pickersEnUS,
        muiGrid: gridEnUS,
        currencies: getCurrencyMap('en_GB'),
        appBundle: enGBBundle,
        defaultCurrency: 'GBP'
    },
    'en-GB-oxendict': {
        label: 'English - UK (Oxford Dictionary)',
        locale: 'en-GB-oxendict',
        dateFns: dateFnsEnGB,
        muiCore: coreEnUS,
        muiPickers: pickersEnUS,
        muiGrid: gridEnUS,
        currencies: getCurrencyMap('en_GB'),
        appBundle: enGBBundle,
        defaultCurrency: 'GBP'
    },
    'en-CA': {
        label: 'English - Canada',
        locale: 'en-CA',
        dateFns: dateFnsEnCA,
        muiCore: coreEnUS,
        muiPickers: pickersEnUS,
        muiGrid: gridEnUS,
        currencies: getCurrencyMap('en_CA'),
        appBundle: enCABundle,
        defaultCurrency: 'CAD'
    },
    'fr-FR': {
        label: 'French - France',
        locale: 'fr-FR',
        dateFns: dateFnsFr,
        muiCore: coreFrFR,
        muiPickers: pickersFrFR,
        muiGrid: gridFrFR,
        currencies: getCurrencyMap('fr_FR'),
        appBundle: frFRBundle,
        defaultCurrency: 'EUR'
    },
    'fr-CA': {
        label: 'French - Canada',
        locale: 'fr-CA',
        dateFns: dateFnsFrCA,
        muiCore: coreFrFR,
        muiPickers: pickersFrFR,
        muiGrid: gridFrFR,
        currencies: getCurrencyMap('fr_CA'),
        appBundle: frCABundle,
        defaultCurrency: 'CAD'
    },
    'de-DE': {
        label: 'German - Germany',
        locale: 'de-DE',
        dateFns: dateFnsDeDE,
        muiCore: coreDeDE,
        muiPickers: pickersDeDE,
        muiGrid: gridDeDE,
        currencies: getCurrencyMap('de_DE'),
        appBundle: deDEBundle,
        defaultCurrency: 'EUR'
    },
    'zh-CN': {
        label: 'Simplified Chinese - China',
        locale: 'zh-CN',
        dateFns: dateFnsZhCN,
        muiCore: coreZhCN,
        muiPickers: pickersZhCN,
        muiGrid: gridZnCH,
        currencies: getCurrencyMap('zh_CN'),
        appBundle: zhCNBundle,
        defaultCurrency: 'CNY'
    },
    'hi-IN': {
        label: 'Hindi - India',
        locale: 'hi-IN',
        dateFns: dateFnsIn,
        muiCore: coreHiIN,
        muiPickers: pickersEnUS,
        muiGrid: gridEnUS,
        currencies: getCurrencyMap('hi_IN'),
        appBundle: hiINBundle,
        defaultCurrency: 'INR'
    }
};

export const LOCALE_REPLACE = {
    fr: 'fr-FR',
    en: 'en-US',
    de: 'de-DE',
    zh: 'zh-CN'
};

/**
 * Fetch browser's locale and if locality is ambiguous replace with specific one, i.e. fr -> fr_FR
 */
export const getBrowserLocale = (): string => {
    const browser = navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language;

    return LOCALE_REPLACE[browser] || browser;
};

export const getL10nDef = (locale = undefined): L10nDefType => {
    const loc = locale || getBrowserLocale();
    return supportedL10n.hasOwnProperty(loc) ? supportedL10n[loc] : supportedL10n[DEFAULT_LOCALE];
};
